import React from 'react';
import { motion } from 'framer-motion';

function getInitials(nombre) {
  if (!nombre) return '?';
  const parts = nombre.trim().split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return nombre[0].toUpperCase();
}

function normalizeOpenAlexAuthorId(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.replace(/^https?:\/\/openalex\.org\//i, '').toUpperCase();
}

function hasOpenAlexIdentifier(investigador) {
  if (normalizeOpenAlexAuthorId(investigador?.open_alex_id)) return true;
  if (!Array.isArray(investigador?.open_alex_ids)) return false;
  return investigador.open_alex_ids.some((id) => Boolean(normalizeOpenAlexAuthorId(id)));
}

function SavedPublicacionCard({ pub, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
          <span className="material-icons-round text-emerald-600 dark:text-emerald-400">article</span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 dark:text-white text-sm leading-snug">
            {pub.titulo}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
            {pub.anio && (
              <span className="inline-flex items-center gap-1">
                <span className="material-icons-round text-[10px]">calendar_today</span>
                {pub.anio}
              </span>
            )}
            {pub.tipo && (
              <span className="inline-flex items-center gap-1">
                <span className="material-icons-round text-[10px]">label</span>
                {pub.tipo}
              </span>
            )}
            {pub.journal && (
              <span className="inline-flex items-center gap-1">
                <span className="material-icons-round text-[10px]">menu_book</span>
                {pub.journal}
              </span>
            )}
          </div>
        </div>
        <div className="flex-shrink-0">
          <span className="inline-flex items-center gap-1 text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-1 rounded-full">
            <span className="material-icons-round text-sm">check_circle</span>
            Guardada
          </span>
        </div>
      </div>
    </motion.div>
  );
}

function StepPublicaciones(props) {
  const {
    universidad,
    investigador,
    savedPublicaciones,
    loading,
    loadingError,
    saveResult,
    onBack,
    onGoExtra,
  } = props;

  const pubs = savedPublicaciones || [];
  const canAddExtraPublications = hasOpenAlexIdentifier(investigador);

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-[#2D5BA6] dark:hover:text-[#5B8BD9] transition-colors mb-4"
        >
          <span className="material-icons-round text-base">arrow_back</span>
          Cambiar investigador
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2D5BA6] to-blue-400 flex items-center justify-center text-white font-bold text-lg shadow-md">
              {getInitials(investigador.nombre)}
            </div>
            <div>
              <p className="font-bold text-slate-900 dark:text-white">{investigador.nombre}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{universidad.name}</p>
            </div>
          </div>

          {!loading && !loadingError && (
            <div className="sm:ml-auto flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <span className="material-icons-round text-[#2D5BA6] text-base">article</span>
              <strong>{pubs.length}</strong> publicaciones guardadas
            </div>
          )}
        </div>

        <h2 className="text-xl font-bold text-slate-900 dark:text-white">Publicaciones guardadas</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Estas son las publicaciones registradas en la base de datos.
        </p>
      </div>

      {saveResult && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 p-4 rounded-xl border bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 flex items-start gap-3"
        >
          <span className="material-icons-round text-2xl text-green-500">check_circle</span>
          <div>
            <p className="font-semibold text-slate-900 dark:text-white">
              {saveResult.saved} guardadas - {saveResult.duplicates} duplicadas
            </p>
          </div>
        </motion.div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-[#2D5BA6] border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-700 dark:text-slate-300 font-semibold">Cargando publicaciones...</p>
          </div>
        </div>
      ) : loadingError ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <span className="material-icons-round text-4xl text-red-400 mb-3 block">error_outline</span>
          <p className="text-red-700 dark:text-red-300 font-semibold">{loadingError}</p>
          <button
            onClick={onBack}
            className="mt-4 px-6 py-2 text-sm font-semibold bg-[#2D5BA6] text-white rounded-lg hover:bg-[#244a8a] transition-colors"
          >
            Volver
          </button>
        </div>
      ) : pubs.length === 0 ? (
        <div className="text-center py-20">
          <span className="material-icons-round text-6xl text-slate-300 dark:text-slate-600">article</span>
          <p className="text-slate-700 dark:text-slate-300 font-semibold mt-4">
            No hay publicaciones guardadas
          </p>
          {onGoExtra && (
            <div className="mt-6">
              <button
                onClick={onGoExtra}
                disabled={!canAddExtraPublications}
                className="px-6 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-900"
              >
                Buscar publicaciones extra
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 py-3 px-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Total: <strong className="text-[#2D5BA6] dark:text-[#5B8BD9]">{pubs.length}</strong> publicaciones
            </span>
            {onGoExtra && (
              <button
                type="button"
                onClick={onGoExtra}
                disabled={!canAddExtraPublications}
                className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-900"
              >
                Buscar publicaciones extra
              </button>
            )}
          </div>

          {!canAddExtraPublications && (
            <p className="mb-4 text-sm text-amber-700 dark:text-amber-300">
              Este investigador necesita un OpenAlex ID antes de añadir publicaciones extra.
            </p>
          )}

          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {pubs.map((pub, idx) => (
              <SavedPublicacionCard key={pub.id || idx} pub={pub} index={idx} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default StepPublicaciones;
